"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import Navbar from "./Navbar";
import MiniFooter from "./MiniFooter";
import { Category, Purpose } from "../types";
import { useCart } from "../context/CartContext";

const CartDrawer = dynamic(() => import("./CartDrawer"), { ssr: false });
const WhatsAppWidget = dynamic(() => import("./WhatsAppWidget"), { ssr: false });

interface ClientLayoutProps {
  children: React.ReactNode;
  categories: Category[];
  purposes: Purpose[];
}

function BareLayout({ children }: Pick<ClientLayoutProps, 'children'>) {
  return (
    <div className="min-h-screen">
      <main className="w-full">{children}</main>
    </div>
  );
}

function ConsultationQueryLayout({ children, categories, purposes }: ClientLayoutProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isConsultationAppFlow =
    (pathname === "/consultations" ||
      pathname.startsWith("/consultations/") ||
      pathname.startsWith("/experts") ||
      pathname.startsWith("/experts/")) &&
    (searchParams?.get("start") === "1" ||
      searchParams?.has("t") ||
      searchParams?.has("id") ||
      searchParams?.has("booking_step") ||
      searchParams?.has("debug_step"));

  if (isConsultationAppFlow) return <BareLayout>{children}</BareLayout>;

  return <StorefrontLayout categories={categories} purposes={purposes}>{children}</StorefrontLayout>;
}

function StorefrontLayout({ children, categories, purposes }: ClientLayoutProps) {
  const pathname = usePathname();
  const { isCartDrawerOpen } = useCart();
  const hideFooter = pathname.startsWith("/product/");

  return (
    <div className="flex min-h-screen flex-col bg-background text-accent md:pb-0">
      <Navbar categories={categories} purposes={purposes} />
      <main className="w-full flex-1 pt-[60px] lg:pt-[72px]">{children}</main>
      {!hideFooter && <MiniFooter />}
      {hideFooter && pathname === "/cart" && <MiniFooter />}
      {isCartDrawerOpen && <CartDrawer />}
      <WhatsAppWidget />
    </div>
  );
}

export default function ClientLayout({ children, categories, purposes }: ClientLayoutProps) {
  const pathname = usePathname();
  const hideLayout =
    pathname.startsWith("/join/") ||
    pathname.startsWith("/kundali-pathsala") ||
    pathname === "/consultationspage" ||
    pathname === "/lp" ||
    pathname.startsWith("/lp/") ||
    pathname.startsWith("/csat") ||
    pathname.startsWith("/delivery-feedback/") ||
    pathname === "/product-usage";

  if (hideLayout) return <BareLayout>{children}</BareLayout>;

  const isConsultationRoute =
    pathname === "/consultations" ||
    pathname.startsWith("/consultations/") ||
    pathname === "/experts" ||
    pathname.startsWith("/experts/");
  if (!isConsultationRoute) {
    return <StorefrontLayout categories={categories} purposes={purposes}>{children}</StorefrontLayout>;
  }

  // Only consultation query flows need useSearchParams. Keeping this boundary
  // here prevents a short layout fallback from wrapping every storefront page.
  return (
    <Suspense fallback={<StorefrontLayout categories={categories} purposes={purposes}>{children}</StorefrontLayout>}>
      <ConsultationQueryLayout categories={categories} purposes={purposes}>
        {children}
      </ConsultationQueryLayout>
    </Suspense>
  );
}
